import React, { useState, useCallback, useEffect, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { useSelector } from "react-redux";
import { useLanguage } from "../../../contexts/LanguageContext";
import { selectUserParams } from "../../../store/slices/userParamsSlice";
import { getWarehouseName } from "../../../utils/warehouseMapping";
import { toastService } from "../../../utils/toastService";
import { getEnv } from "../../../config/runtimeConfig";

const API_BASE = (getEnv("REACT_APP_API_URL") || "").replace(/\/+$/, "");

const TEXT_FIELDS = [
  "kunnr", "customerInfo", "siparisiOlusturanBayi", "eFaturaNo", "irsaliyeNo",
  "siparisKalemKodu", "kalemTanimi", "birim", "birimFiyat", "netTutar",
  "paraBirimi", "musteriSiparisNo", "siparisNumarasiReferansi",
  "nakliyeci", "nakliyeciKonsimento","miktar"
];

const EXACT_FIELDS = [ "cikisDeposu", "siparisOnceligi"];

const DATE_FIELDS = ["irsaliyeTarihi"];

const ShipmentDeliveryList = ({
  data, totalRecords, isLoading, onPage, onSort, lazyParams, sortField, sortOrder,
  activeFilters, onFiltersChange,
  warehouseOptions = [], orderPriorityOptions = []
}) => {
  const { th } = useLanguage();
  const userParams = useSelector(selectUserParams);
  const isInterservice = userParams?.isInterservice === true;
  const safe = lazyParams || { page: 1, rows: 10 };

  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [currentVblen, setCurrentVblen] = useState("");

  useEffect(() => {
    return () => {
      if (invoiceUrl) URL.revokeObjectURL(invoiceUrl);
    };
  }, [invoiceUrl]);

  const handlePage = (e) => onPage?.(e);
  const handleSort = (e) => onSort?.(e);

  const text = (v) => (v == null || v === "" ? "-" : v);
  const num = (v) => (v == null ? "-" : v);
  const date = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };
  const money = (row, amountField, currField) => {
    const amt = row?.[amountField];
    const ccy = row?.[currField] ?? "";
    if (amt == null) return "-";
    return `${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;
  };


  const textMatchModeOptions = useMemo(
    () => [
      { label: th("filter.startsWith"), value: FilterMatchMode.STARTS_WITH },
      { label: th("filter.contains"), value: FilterMatchMode.CONTAINS },
      { label: th("filter.notContains"), value: FilterMatchMode.NOT_CONTAINS },
      { label: th("filter.endsWith"), value: FilterMatchMode.ENDS_WITH },
      { label: th("filter.equals"), value: FilterMatchMode.EQUALS },
      { label: th("filter.notEquals"), value: FilterMatchMode.NOT_EQUALS }
    ],
    [th]
  );

  const dateFilterTemplate = (options) => (
    <Calendar
      value={options.value}
      onChange={(e) => {
        let d = e.value;
        if (d instanceof Date) d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        options.filterApplyCallback(d);
      }}
      dateFormat="dd.mm.yy"
      placeholder={th("common.search")}
      showIcon
      showButtonBar
      className="p-column-filter"
      style={{ minWidth: "11rem" }}
    />
  );

  const dropdownFilterTemplate = (opts, options) => (
    <Dropdown
      value={opts.value}
      options={options}
      onChange={(e) => opts.filterApplyCallback(e.value)}
      placeholder={th("common.search")}
      className="p-column-filter"
      showClear
      style={{ minWidth: "10rem" }}
    />
  );


  const tableFilters = useMemo(() => {
    const f = {};
    TEXT_FIELDS.forEach((field) => {
      f[field] = {
        value: activeFilters?.[field] ?? null,
        matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS
      };
    });
    EXACT_FIELDS.forEach((field) => {
      f[field] = { value: activeFilters?.[field] ?? null, matchMode: FilterMatchMode.EQUALS };
    });
    DATE_FIELDS.forEach((field) => {
      f[field] = { value: activeFilters?.[field] ?? null, matchMode: FilterMatchMode.DATE_IS };
    });
    return f;
  }, [activeFilters]);

  const handleTableFilter = useCallback(
    (e) => {
      const filters = e?.filters || {};
      const updated = { ...(activeFilters || {}) };

      TEXT_FIELDS.forEach((field) => {
        if (field in filters) {
          updated[field] = filters[field]?.value ?? null;
          updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
        }
      });
      [...EXACT_FIELDS, ...DATE_FIELDS].forEach((field) => {
        if (field in filters) updated[field] = filters[field]?.value ?? null;
      });

      onFiltersChange?.(updated);
    },
    [activeFilters, onFiltersChange]
  );

  const textFilterProps = (field, minWidth = "180px") => ({
    filter: true,
    filterField: field,
    filterPlaceholder: th("common.search"),
    showFilterMenu: true,
    filterMatchModeOptions: textMatchModeOptions,
    showClearButton: false,
    style: { minWidth }
  });

  const exactFilterProps = (field, minWidth = "140px") => ({
    filter: true,
    filterField: field,
    filterPlaceholder: th("common.search"),
    showFilterMenu: false,
    showClearButton: false,
    style: { minWidth }
  });


  const makeInvoiceUrl = (vblen) => {
    const base = API_BASE || ""; 
    const url = base ? `${base}/v1/order/GetSapEInvoice` : `/v1/order/GetSapEInvoice`;
    const u = new URL(url, window.location.origin);
    u.searchParams.set("Vblen", vblen);
    return u.toString();
  };

  const resetViewer = () => {
    setInvoiceLoading(false);
    if (invoiceUrl) {
      URL.revokeObjectURL(invoiceUrl);
      setInvoiceUrl("");
    }
  };

  const handleOpenInvoice = useCallback(async (row) => {
    const vbeln = row?.vbeln;
    if (!vbeln || vbeln === "-") return;

    setCurrentVblen(vbeln);
    setShowInvoice(true);
    resetViewer();
    setInvoiceLoading(true);

    const href = makeInvoiceUrl(vbeln);

    try {
      const resp = await fetch(href, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/pdf" },
        cache: "no-store",
      });

      const isPdf = resp.headers.get("content-type")?.includes("application/pdf");

      if (resp.ok && isPdf) {
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        setInvoiceUrl(objUrl);
      } else {
        // ❌ 200 değilse: dialog'u kapat, temizle ve uyarı ver
        const msg = th("order.eInvoiceMissing");
        resetViewer();
        setShowInvoice(false);
        setCurrentVblen("");
        toastService?.warn?.(msg);
      }
    } catch {
      const msg = th("order.eInvoiceMissing");
      resetViewer();
      setShowInvoice(false);
      setCurrentVblen("");
      toastService?.error?.(msg);
    } finally {
      setInvoiceLoading(false);
    }
  }, [th, invoiceUrl]);

  const pdfActionBody = (row) => {
    const eFaturaNo = row?.eFaturaNo;
    // null, boş, veya "-" ise buton gösterme
    if (!eFaturaNo || eFaturaNo === "-") {
      return null;
    }
    return (
      <button
        type="button"
        className="p-button p-button-text p-button-rounded"
        title={th("order.eInvoice")}
        onClick={() => handleOpenInvoice(row)}
      >
        <i className="pi pi-file-pdf" />
      </button>
    );
  };

  return (
    <>
      <DataTable
        value={data}
        loading={isLoading}
        paginator
        lazy
        first={(safe.page - 1) * safe.rows}
        rows={safe.rows}
        totalRecords={totalRecords}
        onPage={handlePage}
        rowsPerPageOptions={[10, 20, 50]}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={handleSort}
        sortMode="single"
        filterDisplay="row"
        filters={tableFilters}
        onFilter={handleTableFilter}
        showGridlines
        stripedRows
        responsiveLayout="scroll"
        emptyMessage={th("shipmentDelivery.emptyMessage") || "No records found."}
        removableSort
      >
        <Column
          field="__actions"
          header={th("order.eInvoice")}
          body={pdfActionBody}
          style={{ textAlign: "center", width: "6rem", minWidth: "6rem" }}
        />

        <Column field="kunnr" header={th("order.customerNo")} sortable
          body={(r) => num(r.kunnr)} {...textFilterProps("kunnr", "150px")} />

        <Column field="customerInfo" header={th("order.customer")} sortable
          body={(r) => num(r.customerInfo)} {...textFilterProps("customerInfo", "220px")} />

        {isInterservice && (
          <Column field="siparisiOlusturanBayi" header={th("shipmentDelivery.orderCreatingDealer")} sortable
            body={(r) => text(r.siparisiOlusturanBayi)} {...textFilterProps("siparisiOlusturanBayi", "220px")} />
        )}

        <Column field="siparisOnceligi" header={th("shipmentDelivery.orderPriority")} sortable
          body={(r) => num(r.siparisOnceligi)}
          style={{ minWidth: "180px" }}
          filter filterField="siparisOnceligi" showFilterMenu={false} showClearButton={false}
          filterElement={(opts) => dropdownFilterTemplate(opts, orderPriorityOptions)} />

        <Column field="irsaliyeTarihi" header={th("shipmentDelivery.waybillDate")} sortable
          body={(r) => date(r.irsaliyeTarihi)}
          style={{ minWidth: "190px" }}
          filter filterField="irsaliyeTarihi" showFilterMenu={false} showClearButton={false}
          filterElement={dateFilterTemplate} />

        <Column field="eFaturaNo" header={th("shipmentDelivery.eInvoiceNo")} sortable
          body={(r) => text(r.eFaturaNo)} {...textFilterProps("eFaturaNo", "190px")} />

        <Column field="irsaliyeNo" header={th("shipmentDelivery.waybillNo")} sortable
          body={(r) => text(r.irsaliyeNo)} {...textFilterProps("irsaliyeNo", "170px")} />

        <Column field="siparisKalemKodu" header={th("shipmentDelivery.orderItemCode")} sortable
          body={(r) => text(r.siparisKalemKodu)} {...textFilterProps("siparisKalemKodu", "180px")} />

        <Column field="kalemTanimi" header={th("shipmentDelivery.itemDescription")} sortable
          body={(r) => text(r.kalemTanimi)} {...textFilterProps("kalemTanimi", "240px")} />

        <Column field="miktar" header={th("shipmentDelivery.quantity")} sortable
          body={(r) => num(r.miktar)} {...textFilterProps("miktar", "140px")} />

        <Column field="birim" header={th("shipmentDelivery.unit")} sortable
          body={(r) => text(r.birim)} {...textFilterProps("birim", "130px")} />

        <Column field="birimFiyat" header={th("shipmentDelivery.unitPrice")} sortable
        body={(r) => money(r, "birimFiyat", "paraBirimi")} {...textFilterProps("birimFiyat", "160px")} />

        <Column field="netTutar" header={th("shipmentDelivery.netAmount")} sortable
        body={(r) => money(r, "netTutar", "paraBirimi")} {...textFilterProps("netTutar", "160px")} />

        <Column field="paraBirimi" header={th("shipmentDelivery.currency")} sortable
          body={(r) => text(r.paraBirimi)} {...textFilterProps("paraBirimi", "130px")} />

        <Column field="cikisDeposu" header={th("shipmentDelivery.exitWarehouse")} sortable
          body={(r) => text(getWarehouseName(r.cikisDeposu))}
          style={{ minWidth: "180px" }}
          filter filterField="cikisDeposu" showFilterMenu={false} showClearButton={false}
          filterElement={(opts) => dropdownFilterTemplate(opts, warehouseOptions)} />

        <Column field="musteriSiparisNo" header={th("shipmentDelivery.customerOrderNo")} sortable
          body={(r) => text(r.musteriSiparisNo)} {...textFilterProps("musteriSiparisNo", "190px")} />

        <Column field="siparisNumarasiReferansi" header={th("shipmentDelivery.orderNumber")} sortable
          body={(r) => text(r.siparisNumarasiReferansi)} {...textFilterProps("siparisNumarasiReferansi", "200px")} />

        <Column field="nakliyeci" header={th("shipmentDelivery.carrier")} sortable
          body={(r) => text(r.nakliyeci)} {...textFilterProps("nakliyeci", "180px")} />

        <Column field="nakliyeciKonsimento" header={th("shipmentDelivery.carrierConsignment")} sortable
          body={(r) => text(r.nakliyeciKonsimento)} {...textFilterProps("nakliyeciKonsimento", "200px")} />
      </DataTable>

    <Dialog
      header={(th("order.eInvoice")) + (currentVblen ? ` #${currentVblen}` : "")}
      visible={showInvoice}
      style={{ width: "80vw", maxWidth: 1100 }}
      modal
      onHide={() => {
        setShowInvoice(false);
        resetViewer();
        setCurrentVblen("");
      }}
      contentStyle={{ padding: 0 }}
    >
      {invoiceLoading && (
        <div className="flex items-center justify-center py-8">
          <ProgressSpinner />
        </div>
      )}

      {!invoiceLoading && invoiceUrl && (
        <iframe
          title={`invoice-${currentVblen}`}
          src={invoiceUrl}
          style={{ width: "100%", height: "80vh", border: "none" }}
        />
      )}
    </Dialog>
  </>
  );
};

export default ShipmentDeliveryList;